import { type NavigationGuardReturn, type RouteLocationNormalizedLoaded, type RouteLocationNormalized, START_LOCATION } from 'vue-router'
import configResolver, { pathCombine } from '@/common/helpers/configResolver'
import { initAppSessionSettings } from '@/common/services/ApplicationService'
import { triggerOidcChangePassword } from '@/common/services/AuthenticationService'
import { type NavigationGuardWithoutNext } from '@/app/types/router'

import { useContextualSearchStore } from '@/search/stores/contextualSearchStore'
import { useSessionStore } from '@/common/stores/sessionStore'
import { useBreaksStore } from '@/agent/stores/breakStore'
import { useCaseStore } from '@/agent/stores/caseStore'
import { useSearchStore } from '@/search/stores/searchStore'
import { useNavigationStore } from '@/common/stores/navigationStore'

import { clear as clearLocalData } from '@/common/services/LocalStorageService'
import { checkNavigationConsistent, handleNavigation, NAVIGATION_HISTORY_KEY, resetNavigationHistory } from '@/common/services/NavigationService'
import localizationItemServerData from '@/common/services/serverData/localizationItemServerData'

import { PrefixTranslationsEnum } from '@/common/types/i18n'
import { useRolesStore } from '@/common/stores/rolesStore'
import { useCompanyStore } from '@/agent/stores/companyStore'
import { AuthoritiesEnum } from '@/common/types/session'

async function handleChangePasswordRoute(originPath: string, oidcEnabled: boolean) {
  if (!oidcEnabled) return

  await triggerOidcChangePassword(pathCombine([configResolver.root, originPath]))
}

export const contextualSearchBeforeEnter = async (): Promise<NavigationGuardReturn> => {
  useContextualSearchStore().resetContextualSearch()
}

export const changePasswordRouteBeforeEnter = async (
  _: RouteLocationNormalized,
  from: RouteLocationNormalizedLoaded,
): Promise<NavigationGuardReturn> => {
  const sessionStore = useSessionStore()
  await handleChangePasswordRoute(from.path, sessionStore.oidcEnabled)
}

export const agentBeforeEnter: NavigationGuardWithoutNext = async (_, from) => {
  if (from.meta.agent) return

  clearLocalData(['session', 'parameters', NAVIGATION_HISTORY_KEY])

  if (from?.meta?.admin) {
    initAppSessionSettings()

    const searchStore = useSearchStore()
    searchStore.$reset()
  }

  /**
   * Prefetch and merge translations asynchronously
   */
  localizationItemServerData.fetch({
    prefixes: [PrefixTranslationsEnum.TYPOLOGY],
  })
  localizationItemServerData.fetch({
    prefixes: [PrefixTranslationsEnum.REASON],
  })
  localizationItemServerData.fetch({
    prefixes: [PrefixTranslationsEnum.SKILL],
  })

  /**
   * Hydrate the app synchronously for data needed immediately
   */
  const hydrationRequests = [
    useRolesStore().loadRoles(),
    useBreaksStore().load(),
    localizationItemServerData.fetch({
      prefixes: [PrefixTranslationsEnum.USERSTATE, PrefixTranslationsEnum.SCREEN, PrefixTranslationsEnum.SPACE],
    }),
  ]
  await Promise.all(hydrationRequests)
}

export const caseBeforeEnter = () => {
  if (useSessionStore().getAuthoritySet.has(AuthoritiesEnum.COMPANY_SEARCH)) {
    useCompanyStore().$reset()
  }
  useCaseStore().$reset()
  useNavigationStore().$reset()
}

export const navigationBeforeResolve: NavigationGuardWithoutNext = async (to: RouteLocationNormalized, from: RouteLocationNormalizedLoaded) => {
  if (to.query.mergedCustomerSucceed) {
    // after a merge customer between two existing customers
    // we clear the navigation history because the last path can be the page of the customer deleted
    resetNavigationHistory()
    return
  }
  if (from.meta?.admin || to.meta?.admin || from === START_LOCATION || from.path === to.path) return

  if (to.meta?.allowBackNavigation) {
    if (!from.meta?.skipHistory) {
      handleNavigation(from.path)
    }
    checkNavigationConsistent(to.path)
  } else {
    resetNavigationHistory()
  }
}
